import Session from '../core/session'
import Config from '../core/config'

export class ErrorCollector {
  private static instance: ErrorCollector
  private isTracking: boolean = false

  private constructor() {}

  public static getInstance(): ErrorCollector {
    if (!ErrorCollector.instance) {
      ErrorCollector.instance = new ErrorCollector()
    }
    return ErrorCollector.instance
  }

  /**
   * Start tracking errors
   */
  public start(): void {
    if (!Config.get('captureErrors')) {
      return
    }

    if (this.isTracking) {
      return
    }

    this.isTracking = true

    // Track JavaScript errors
    window.addEventListener('error', this.handleError.bind(this))

    // Track unhandled promise rejections
    window.addEventListener('unhandledrejection', this.handleRejection.bind(this))

    // Track console errors (optional)
    this.interceptConsoleError()
  }

  /**
   * Handle JavaScript errors
   */
  private handleError(event: ErrorEvent): void {
    Session.trackEvent('error', {
      error_type: 'javascript',
      message: event.message,
      filename: event.filename,
      line_number: event.lineno,
      column_number: event.colno,
      stack: event.error?.stack || '',
      severity: 'high'
    })

    if (Config.isDebug()) {
      console.error('[RadarSnap] JavaScript error tracked:', event.message)
    }
  }

  /**
   * Handle unhandled promise rejections
   */
  private handleRejection(event: PromiseRejectionEvent): void {
    Session.trackEvent('error', {
      error_type: 'promise_rejection',
      message: event.reason?.message || String(event.reason),
      stack: event.reason?.stack || '',
      severity: 'medium'
    })

    if (Config.isDebug()) {
      console.error('[RadarSnap] Promise rejection tracked:', event.reason)
    }
  }

  /**
   * Intercept console.error to track application errors
   */
  private interceptConsoleError(): void {
    const originalError = console.error
    console.error = (...args: any[]) => {
      // Call original console.error
      originalError.apply(console, args)

      // Track in analytics
      Session.trackEvent('error', {
        error_type: 'console',
        message: args.map(arg => String(arg)).join(' '),
        severity: 'low'
      })
    }
  }

  /**
   * Stop tracking
   */
  public stop(): void {
    this.isTracking = false
    window.removeEventListener('error', this.handleError.bind(this))
    window.removeEventListener('unhandledrejection', this.handleRejection.bind(this))
  }
}

export default ErrorCollector.getInstance()